import { PlatformProvider } from "@birdwatch/client";
import { Toaster } from "@birdwatch/ui/toast";
import { ClerkProvider } from "@clerk/clerk-react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { createRootRoute, HeadContent, Outlet } from "@tanstack/react-router";
import { ErrorBoundary } from "react-error-boundary";
import NotFound from "@/components/not-found";
import { ThemeProvider } from "@/components/providers";
import { env } from "@/env";
import {
  AppUpdateModal,
  AppVersionWatcher,
} from "@/features/shared/app-version";
import { ErrorFallback } from "@/features/shared/error-fallback";
import { GlobalErrorHandler } from "@/features/shared/handle-error";
import { MediaUploadTracker } from "@/features/shared/media-upload";
import { RealtimeRefreshRoot } from "@/features/shared/realtime-refresh";
import { RealtimeRelayProvider } from "@/features/shared/realtime-relay";
import { redirectIfMemberLegacyPath } from "@/libs/member-legacy-path-redirect";

if (!env.PUBLIC_CLERK_PUBLISHABLE_KEY) {
  throw new Error("Add your Clerk Publishable Key to the .env file");
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      refetchOnReconnect: false,
      staleTime: 1000 * 60, // 1 minutes
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), //0 -> 1000s, 1 -> 2000ms, 2 -> 4000ms
    },
  },
});

export const Route = createRootRoute({
  component: () => (
    <>
      <HeadContent />
      <RootComponent />
    </>
  ),
  beforeLoad: ({ location }) => {
    redirectIfMemberLegacyPath({
      pathname: location.pathname,
      searchStr: location.searchStr,
    });
  },
  notFoundComponent: NotFound,
});

/**
 * Creates an Indexed DB persister
 * @see https://developer.mozilla.org/en-US/docs/Web/API/IndexedDB_API
 */

const DevTools =
  process.env.NODE_ENV === "development" ? (
    <ReactQueryDevtools initialIsOpen={false} />
  ) : null;

function RootComponent() {
  // Satellite mode: when this deployment runs a production Clerk instance on a
  // non-primary domain, `domain` marks it as the satellite and sign-in is
  // delegated to the primary instance. Otherwise Clerk stays a primary instance
  // that signs in on the local /sign-in route. `isSatellite`/`domain` must be
  // spread conditionally — Clerk's types forbid `isSatellite` unless `domain`
  // (or a proxy) is also set.
  const clerkMultiDomainProps =
    env.PUBLIC_CLERK_IS_SATELLITE && env.PUBLIC_CLERK_DOMAIN
      ? { isSatellite: true as const, domain: env.PUBLIC_CLERK_DOMAIN }
      : {};
  const signInUrl = env.PUBLIC_CLERK_IS_SATELLITE
    ? `${env.PUBLIC_CLERK_SIGN_IN_URL.replace(/\/$/, "")}/sign-in`
    : "/sign-in";

  return (
    <ClerkProvider
      publishableKey={env.PUBLIC_CLERK_PUBLISHABLE_KEY}
      signInUrl={signInUrl}
      {...clerkMultiDomainProps}
    >
      <QueryClientProvider client={queryClient}>
        <PlatformProvider baseUrl={`${env.PUBLIC_PLATFORM_URL}/api/v5`}>
          <RealtimeRelayProvider>
            <RealtimeRefreshRoot>
              <MediaUploadTracker />
              <GlobalErrorHandler>
                <ErrorBoundary
                  fallbackRender={({ error }) => (
                    <ErrorFallback error={error} fullScreen />
                  )}
                >
                  <ThemeProvider
                    attribute="class"
                    defaultTheme="system"
                    enableSystem
                    disableTransitionOnChange
                  >
                    <Outlet />
                    {/* Every tab, signed in or not, hears a release: the
                        channel is public, so guest and sign-in pages are
                        offered the reload too. */}
                    <AppVersionWatcher />
                    <AppUpdateModal />
                    <Toaster />
                  </ThemeProvider>
                </ErrorBoundary>
              </GlobalErrorHandler>
            </RealtimeRefreshRoot>
          </RealtimeRelayProvider>
        </PlatformProvider>
        {DevTools}
      </QueryClientProvider>
    </ClerkProvider>
  );
}
